import { useEffect, useRef, useState } from "react";
import { Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Skeleton, Stack, Typography } from "@mui/material";
import "../../design-system/hpoint/table-tennis.css";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import { useNavigate } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import { HttpMainApi } from "../../interface/main-api";
import { userState } from "../../interface/MainInterface";

interface TableTennisPolicy {
  availabilityStatus: "OPERATING" | "UNDER_REPAIR" | "OPENING_SOON";
  bookingEnabled: boolean;
  openTime: string;
  closeTime: string;
  lastStartTime: string;
  maxAdvanceDays: number;
  maxContiguousHours: number;
  hourlyPrice: number | null;
  serverNow: string;
}
interface DayAvailability { date: string; reservedSlots: string[]; serverNow: string }

const mainApi = new HttpMainApi();
const slots = Array.from({ length: 12 }, (_, index) => `${index + 10}:00`);
const addHour = (slot: string) => `${Number(slot.slice(0, 2)) + 1}:00`;
const formatDateKey = (date: Date) =>
  `${date.getFullYear()}${String(date.getMonth() + 1).padStart(2, "0")}${String(date.getDate()).padStart(2, "0")}`;
const parseDateKey = (key: string) => new Date(+key.slice(0, 4), +key.slice(4, 6) - 1, +key.slice(6, 8));
const dateLabel = (key: string) => `${key.slice(0, 4)}년 ${key.slice(4, 6)}월 ${key.slice(6, 8)}일`;
const slotTimestamp = (key: string, slot: string) =>
  Date.parse(`${key.slice(0, 4)}-${key.slice(4, 6)}-${key.slice(6, 8)}T${slot}:00+09:00`);
const validServerTime = (value: unknown): value is string => typeof value === "string" && Number.isFinite(Date.parse(value));

const ReservationTableTennis = ({ userState }: { userState: userState }) => {
  const navigate = useNavigate();
  const [policy, setPolicy] = useState<TableTennisPolicy | null>(null);
  const [policyLoading, setPolicyLoading] = useState(true);
  const [policyReload, setPolicyReload] = useState(0);
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedSlots, setSelectedSlots] = useState<string[]>([]);
  const [availability, setAvailability] = useState<DayAvailability | null>(null);
  const [availabilityLoading, setAvailabilityLoading] = useState(false);
  const [availabilityError, setAvailabilityError] = useState(false);
  const [availabilityReload, setAvailabilityReload] = useState(0);
  const [error, setError] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);
  const request = useRef<{ payload: string; id: string } | null>(null);
  const [serverNow, setServerNow] = useState(0);
  const clock = useRef({ serverMs: 0, receivedAt: 0 });

  // Advance the server clock while the screen is open, independent of the device's timezone.
  const syncClock = (value: string) => {
    clock.current = { serverMs: Date.parse(value), receivedAt: Date.now() };
    setServerNow(clock.current.serverMs);
  };
  useEffect(() => {
    const timer = window.setInterval(() => {
      if (clock.current.serverMs) setServerNow(clock.current.serverMs + Date.now() - clock.current.receivedAt);
    }, 1000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    let active = true;
    setPolicyLoading(true);
    setSelectedSlots([]);
    const load = async () => {
      try {
        const res = await mainApi.get_table_tennis_policy({});
        if (!active) return;
        const next = res.response as TableTennisPolicy | undefined;
        if (res.code !== "200" || !next || !validServerTime(next.serverNow)
          || !["OPERATING", "UNDER_REPAIR", "OPENING_SOON"].includes(next.availabilityStatus)
          || typeof next.bookingEnabled !== "boolean" || next.openTime !== "10:00"
          || next.closeTime !== "22:00" || next.lastStartTime !== "21:00" || next.maxAdvanceDays !== 30
          || next.maxContiguousHours !== 3
          || !(next.hourlyPrice === null && !next.bookingEnabled
            || Number.isSafeInteger(next.hourlyPrice) && Number(next.hourlyPrice) > 0)) {
          throw new Error("Invalid policy");
        }
        setPolicy(next);
        syncClock(next.serverNow);
        const today = new Date(Date.parse(next.serverNow) + 9 * 3600000).toISOString().slice(0, 10).replace(/-/g, "");
        setSelectedDate(previous => previous || today);
      } catch {
        if (active) setPolicy(null);
      } finally {
        if (active) setPolicyLoading(false);
      }
    };
    void load();
    return () => { active = false; };
  }, [policyReload]);

  const bookingEnabled = !policyLoading && policy?.availabilityStatus === "OPERATING" && policy.bookingEnabled;
  useEffect(() => {
    let active = true;
    setAvailability(null);
    setAvailabilityError(false);
    setSelectedSlots([]);
    if (!bookingEnabled || !selectedDate) {
      setAvailabilityLoading(false);
      return;
    }
    setAvailabilityLoading(true);
    const load = async () => {
      try {
        const res = await mainApi.get_table_tennis_day_availability({ date: selectedDate });
        if (!active) return;
        const next = res.response as DayAvailability | undefined;
        if (res.code !== "200" || !next || next.date !== selectedDate || !Array.isArray(next.reservedSlots)
          || !next.reservedSlots.every(slot => slots.includes(slot)) || !validServerTime(next.serverNow)) {
          throw new Error("Invalid availability");
        }
        setAvailability(next);
        syncClock(next.serverNow);
      } catch {
        if (active) setAvailabilityError(true);
      } finally {
        if (active) setAvailabilityLoading(false);
      }
    };
    void load();
    return () => { active = false; };
  }, [bookingEnabled, selectedDate, availabilityReload]);

  const today = serverNow ? new Date(serverNow + 9 * 3600000).toISOString().slice(0, 10).replace(/-/g, "") : "";
  const minDate = today ? parseDateKey(today) : undefined;
  const maxDate = minDate ? new Date(minDate.getFullYear(), minDate.getMonth(), minDate.getDate() + (policy?.maxAdvanceDays ?? 30)) : undefined;
  const dateInRange = !!minDate && !!maxDate && parseDateKey(selectedDate) >= minDate && parseDateKey(selectedDate) <= maxDate;
  const availabilityReady = bookingEnabled && dateInRange && !availabilityLoading && !availabilityError && availability?.date === selectedDate;
  const isPast = (slot: string) => slotTimestamp(selectedDate, slot) + 60 * 60 * 1000 <= serverNow;
  const canBook = availabilityReady && selectedSlots.length > 0
    && selectedSlots.length <= (policy?.maxContiguousHours ?? 3) && !selectedSlots.some(isPast);
  const estimatedPrice = selectedSlots.length * (policy?.hourlyPrice ?? 0);
  const buttonLabel = policy?.availabilityStatus === "UNDER_REPAIR" ? "수리중"
    : policy?.availabilityStatus === "OPENING_SOON" ? "오픈 예정" : "예약 신청";

  const toggleSlot = (slot: string) => {
    if (!availabilityReady || saving || isPast(slot) || availability?.reservedSlots.includes(slot)) return;
    const next = (selectedSlots.includes(slot) ? selectedSlots.filter(item => item !== slot) : [...selectedSlots, slot]).sort();
    if (!next.every((value, index) => index === 0 || Number(value.slice(0, 2)) - Number(next[index - 1].slice(0, 2)) === 1)) {
      setError("연속된 시간만 선택해 주세요.");
      return;
    }
    if (next.length > (policy?.maxContiguousHours ?? 3)) {
      setError("한 번에 최대 3시간까지 예약할 수 있습니다.");
      return;
    }
    setError("");
    setSelectedSlots(next);
  };

  const save = async () => {
    if (!canBook || savingRef.current) return;
    savingRef.current = true;
    setSaving(true);
    setError("");
    const payload = JSON.stringify([userState.userId, selectedDate, selectedSlots]);
    if (request.current?.payload !== payload) request.current = { payload, id: uuidv4() };
    try {
      const res = await mainApi.create_table_tennis_reservation({
        reservDate: selectedDate, slots: selectedSlots, requestId: request.current.id,
        userId: userState.userId, userName: userState.userName, userAddr: userState.readAddress,
      });
      if (res.code === "200") {
        setConfirmOpen(false);
        navigate("/reservationtabletennisend", {
          state: { createdReservation: { reservationId: res.response.reservationId,
            userId: userState.userId, slots: selectedSlots } },
        });
      } else if (res.code === "301" || res.code === "606") {
        setConfirmOpen(false);
        setSelectedSlots([]);
        request.current = null;
        setError(res.code === "301" ? "선택한 시간에 다른 예약이 완료되었습니다. 시간을 다시 선택해 주세요." : "이미 시작된 시간이 포함되어 있습니다. 시간을 다시 선택해 주세요.");
        setAvailabilityReload(value => value + 1);
      } else if (res.code === "608") {
        setConfirmOpen(false);
        setError("시설 운영 상태가 변경되었습니다. 운영 안내를 확인해 주세요.");
        setPolicyReload(value => value + 1);
      } else {
        setError(res.response?.error_msg || "예약을 완료하지 못했습니다. 다시 시도해 주세요.");
      }
    } catch {
      setError("예약 결과를 확인하지 못했습니다. 같은 선택으로 다시 시도해 주세요.");
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  return (
    <Stack className="hpoint-renewal hp-reservation-page hp-table-tennis" spacing={2} sx={{ maxWidth: "40rem", mx: "auto", p: 2 }}>
      <Box component="header" className="hp-screen-hero">
        <Typography component="h1" className="hp-page-title">탁구장 예약</Typography>
        <Typography className="hp-page-description">이용할 날짜와 시간을 선택하고 예약 내용을 확인하세요.</Typography>
      </Box>
      {!policyLoading && policy?.availabilityStatus === "OPENING_SOON" && (
        <Box component="section" className="hp-table-tennis__opening" aria-label="탁구장 오픈 안내" role="status">
          <span className="hp-table-tennis__badge">오픈 예정</span>
          <Typography component="h2">탁구장 오픈을 준비하고 있어요</Typography>
          <Typography>오픈일 미정</Typography>
          <Typography variant="body2">아직 예약할 수 없습니다. 오픈일은 추후 안내할 예정입니다.</Typography>
        </Box>
      )}
      <Alert severity="info">
        <Stack spacing={0.25}>
          <Typography variant="body2">매일 10:00 ~ 22:00 운영</Typography>
          <Typography variant="body2">1시간 {policy?.hourlyPrice ? `${policy.hourlyPrice.toLocaleString("ko-KR")}원` : "요금 확인 중"}</Typography>
          <Typography variant="body2">1시간 단위로 연속 예약 가능합니다.</Typography>
        </Stack>
      </Alert>
      {!policyLoading && policy?.availabilityStatus === "UNDER_REPAIR" && <Alert severity="warning" role="status">
        현재 수리중입니다. 예약 신청이 잠시 중단되었습니다.
      </Alert>}
      {!policyLoading && policy?.hourlyPrice === null && <Alert severity="error">탁구장 이용요금을 확인하지 못했습니다. 잠시 후 다시 시도해 주세요.
        <Button onClick={() => setPolicyReload(value => value + 1)}>정책 다시 불러오기</Button>
      </Alert>}
      {!policyLoading && !policy && <Alert severity="error">
        예약 정책을 불러오지 못했습니다.
        <Button onClick={() => setPolicyReload(value => value + 1)}>정책 다시 불러오기</Button>
      </Alert>}
      <Box className="hp-card hp-calendar-card" sx={{ p: 2, "& .hp-calendar .react-calendar__tile--active": { color: "#fff" } }}>
        <Typography component="h2" variant="subtitle1" sx={{ mb: 1 }}>날짜 선택</Typography>
        {policyLoading ? <CircularProgress size={24} aria-label="예약 정책 조회 중" /> : policy && <Calendar
          calendarType="gregory" className="custom-calendar hp-calendar" minDate={minDate} maxDate={maxDate}
          value={selectedDate ? parseDateKey(selectedDate) : minDate} formatDay={(_locale, date) => String(date.getDate())}
          tileDisabled={() => !bookingEnabled || saving}
          onClickDay={date => {
            if (!bookingEnabled || saving) return;
            setSelectedDate(formatDateKey(date)); setSelectedSlots([]); setError("");
          }}
        />}
      </Box>
      <Box className="hp-card" sx={{ p: 2 }}>
        <Typography component="h2" variant="subtitle1">시간 선택</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>한 번에 최대 3시간까지 연속 예약할 수 있어요.</Typography>
        {availabilityError && <Alert severity="error" sx={{ mb: 1 }}>
          예약 현황을 불러오지 못했습니다.
          <Button onClick={() => setAvailabilityReload(value => value + 1)}>예약 현황 다시 불러오기</Button>
        </Alert>}
        <Box data-testid="table-tennis-time-grid" aria-busy={availabilityLoading}
          sx={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gridAutoRows: "var(--hp-control-height-sm)", gap: 1 }}>
          {slots.map(slot => {
            if (availabilityLoading) return <Skeleton key={slot} variant="rounded" height="100%" aria-hidden="true" />;
            const isReserved = availability?.reservedSlots.includes(slot);
            return <Button
              key={slot} className="hp-table-tennis-time-slot"
              aria-label={`${slot} ~ ${addHour(slot)}${isReserved ? " 예약됨" : ""}`} aria-pressed={selectedSlots.includes(slot)}
              disabled={!availabilityReady || saving || isReserved || isPast(slot)}
              variant={selectedSlots.includes(slot) ? "contained" : "outlined"} onClick={() => toggleSlot(slot)}>
              <span className="hp-table-tennis-time-slot__time">{slot} ~ {addHour(slot)}</span>
              {isReserved && <span className="hp-table-tennis-time-slot__badge" aria-hidden="true">예약</span>}
            </Button>;
          })}
        </Box>
      </Box>
      {error && !confirmOpen && <Alert severity="error">{error}</Alert>}
      {selectedSlots.some(isPast) && !confirmOpen && <Alert severity="warning">
        선택한 시간의 종료 시각이 지났습니다. 시간을 다시 선택해 주세요.
        <Button disabled={saving} onClick={() => setSelectedSlots([])}>시간 다시 선택</Button>
      </Alert>}
      <Box component="section" className="hp-table-tennis__summary" aria-label="선택한 예약 정보" aria-live="polite">
        <Typography component="h2" variant="subtitle1">예약 내용 확인</Typography>
        <dl>
          <div><dt>이용 날짜</dt><dd>{selectedDate ? dateLabel(selectedDate) : "날짜를 선택해 주세요."}</dd></div>
          <div><dt>이용시간</dt><dd>{selectedSlots.length ? `${selectedSlots[0]} ~ ${addHour(selectedSlots[selectedSlots.length - 1])} · ${selectedSlots.length}시간` : "시간을 선택해 주세요."}</dd></div>
          <div><dt>이용요금</dt><dd>{policy?.hourlyPrice ? `${estimatedPrice.toLocaleString("ko-KR")}원` : "요금 확인 불가"}</dd></div>
        </dl>
      </Box>
      <Stack spacing={1}>
        <Button className="hp-button hp-button--primary hp-sticky-cta" variant="contained" disabled={!canBook || saving}
          onClick={() => { setError(""); setConfirmOpen(true); }}>{buttonLabel}</Button>
        <Button variant="outlined" disabled={saving} onClick={() => navigate("/reservationtabletennisend")}>예약 내역</Button>
      </Stack>
      <Dialog className="hp-dialog hp-book-cafe-confirm-dialog" open={confirmOpen} onClose={() => !saving && setConfirmOpen(false)}
        PaperProps={{ "aria-label": "탁구장을 예약하시겠어요?", className: "hp-dialog__paper hp-book-cafe-confirm-dialog__paper" }}>
        <DialogTitle className="hp-book-cafe-confirm-dialog__header">
          <Typography component="span" className="hp-book-cafe-confirm-dialog__title">탁구장을 예약하시겠어요?</Typography>
        </DialogTitle>
        <DialogContent className="hp-book-cafe-confirm-dialog__body">
          <Box className="hp-book-cafe-confirm-dialog__details">
            <Box className="hp-book-cafe-confirm-dialog__detail">
              <Typography component="span" className="hp-book-cafe-confirm-dialog__label">이용 날짜</Typography>
              <Typography component="strong" className="hp-book-cafe-confirm-dialog__date">{dateLabel(selectedDate)}</Typography>
            </Box>
            <Box className="hp-book-cafe-confirm-dialog__detail">
              <Typography component="span" className="hp-book-cafe-confirm-dialog__label">선택 시간</Typography>
              <Box className="hp-book-cafe-confirm-dialog__times">{selectedSlots.map(slot =>
                <Box component="span" className="hp-book-cafe-confirm-dialog__time" key={slot}>{slot}~{addHour(slot)}</Box>)}</Box>
            </Box>
          </Box>
          <Box className="hp-book-cafe-confirm-dialog__total">
            <Box className="hp-book-cafe-confirm-dialog__total-copy"><Typography component="span">이용요금</Typography><Typography component="small">{selectedSlots.length}시간 이용</Typography></Box>
            <Typography component="strong" className="hp-book-cafe-confirm-dialog__price">{estimatedPrice.toLocaleString("ko-KR")}원</Typography>
          </Box>
          {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
          {!canBook && !saving && <Alert severity="warning" sx={{ mt: 2 }}>선택한 시간을 예약할 수 없습니다. 시간을 다시 선택해 주세요.</Alert>}
        </DialogContent>
        <DialogActions className="hp-table-tennis__confirm-actions">
          <Button disabled={saving} onClick={() => setConfirmOpen(false)}>취소</Button>
          <Button variant="contained" disabled={saving || !canBook} onClick={save}>{saving ? "예약 중…" : "예약 확정"}</Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default ReservationTableTennis;
